import { useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, Text, TextInput, View } from 'react-native';

import {
  addMemberEmail,
  fetchMemberManagement,
  removeMember,
  removePendingMember,
  type PendingMember,
} from '@/lib/blocks/api';
import type { MemberRecord } from '@/lib/blocks/types';

export default function MembersSettingsScreen() {
  const [members, setMembers] = useState<MemberRecord[]>([]);
  const [pending, setPending] = useState<PendingMember[]>([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function loadMembers(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMemberManagement();
      setMembers(data.members);
      setPending(data.pending);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadMembers();
  }, []);

  async function onAddEmail(): Promise<void> {
    setError(null);
    try {
      await addMemberEmail(email.trim(), 'member');
      setEmail('');
      await loadMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add email');
    }
  }

  return (
    <ScrollView className="flex-1 bg-surface px-6 py-8">
      <Text className="text-2xl font-semibold text-fg">Members</Text>
      <Text className="mt-2 text-base text-muted">
        Add email addresses that are allowed to sign in. Only listed members can use the app.
      </Text>

      <View className="mt-6 flex-row items-center gap-2">
        <TextInput
          autoCapitalize="none"
          className="min-w-0 flex-1 rounded-lg border border-border px-3 py-2 text-base"
          keyboardType="email-address"
          onChangeText={setEmail}
          placeholder="email@example.com"
          value={email}
        />
        <Pressable className="rounded-lg bg-accent px-4 py-2" onPress={() => void onAddEmail()}>
          <Text className="text-base text-white">Add</Text>
        </Pressable>
      </View>

      {error ? <Text className="mt-3 text-sm text-danger">{error}</Text> : null}

      {loading ? (
        <ActivityIndicator className="mt-6" />
      ) : (
        <View className="mt-8">
          <Text className="mb-3 text-sm font-medium text-fg-muted">Active members</Text>
          {members.length === 0 ? (
            <Text className="text-sm text-muted">No active members yet.</Text>
          ) : (
            members.map((member) => (
              <View
                key={member.id}
                className="mb-3 flex-row items-center justify-between rounded-lg border border-border px-3 py-3"
              >
                <View>
                  <Text className="text-base text-fg">
                    {member.displayName}
                    {member.email ? ` · ${member.email}` : ''}
                  </Text>
                  <Text className="text-xs text-muted">{member.role}</Text>
                </View>
                <Pressable onPress={() => void removeMember(member.id).then(loadMembers)}>
                  <Text className="text-sm text-danger">Remove</Text>
                </Pressable>
              </View>
            ))
          )}

          <Text className="mb-3 mt-8 text-sm font-medium text-fg-muted">Pending (not signed in yet)</Text>
          {pending.length === 0 ? (
            <Text className="text-sm text-muted">No pending emails.</Text>
          ) : (
            pending.map((entry) => (
              <View
                key={entry.id}
                className="mb-3 flex-row items-center justify-between rounded-lg border border-border px-3 py-3"
              >
                <View>
                  <Text className="text-base text-fg">{entry.email}</Text>
                  <Text className="text-xs text-muted">{entry.role}</Text>
                </View>
                <Pressable onPress={() => void removePendingMember(entry.id).then(loadMembers)}>
                  <Text className="text-sm text-danger">Remove</Text>
                </Pressable>
              </View>
            ))
          )}
        </View>
      )}
    </ScrollView>
  );
}
